import React, { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { ArrowLeftIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline'
import PageHeader from '../../components/PageHeader'
import { getLocalApiUrl, authFetch } from '../../config/api'

const inputClass = 'w-full border border-gray-300 rounded-md px-3 py-2 text-sm'
const labelClass = 'block text-xs font-medium text-gray-600 mb-1'

function slugId(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
}

function AmountField({ amounts, onChange }) {
  const [text, setText] = useState((amounts || []).join(', '))
  useEffect(() => {
    setText((amounts || []).join(', '))
  }, [amounts])
  return (
    <input
      className={inputClass}
      value={text}
      onChange={(e) => setText(e.target.value)}
      onBlur={() => onChange(
        text.split(',').map((n) => Number(n.trim())).filter((n) => Number.isFinite(n) && n > 0)
      )}
    />
  )
}

function FloorRolePicker({ title, hint, roles, selected, onChange }) {
  const chosen = new Set(selected || [])
  return (
    <div className="md:col-span-2">
      <div className={labelClass}>{title}</div>
      <p className="text-xs text-gray-500 mb-2">{hint}</p>
      {roles.length === 0 ? (
        <p className="text-sm text-gray-500">No roles are available to assign yet.</p>
      ) : (
        <div className="flex flex-wrap gap-x-4 gap-y-2">
          {roles.map((role) => (
            <label key={role.role_key} className="inline-flex items-center gap-2 text-sm text-gray-800">
              <input
                type="checkbox"
                checked={chosen.has(role.role_key)}
                onChange={(e) => {
                  const next = new Set(chosen)
                  if (e.target.checked) next.add(role.role_key)
                  else next.delete(role.role_key)
                  onChange([...next])
                }}
              />
              {role.display_name || role.role_key}
            </label>
          ))}
        </div>
      )}
    </div>
  )
}

function OptionListEditor({ title, rows, onChange, withDescription = false, withOrder = false }) {
  const update = (index, patch) => {
    onChange(rows.map((row, i) => (i === index ? { ...row, ...patch } : row)))
  }
  return (
    <div className="space-y-2">
      <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">{title}</div>
      {rows.map((row, index) => (
        <div key={`${row.id || 'row'}-${index}`} className="flex items-center gap-2">
          {withOrder && (
            <input
              type="number"
              min="1"
              className="w-16 shrink-0 border border-gray-300 rounded-md px-2 py-2 text-sm"
              value={row.order ?? index + 1}
              aria-label="Order"
              onChange={(e) => update(index, { order: Number(e.target.value) })}
            />
          )}
          <input
            className={`${inputClass} min-w-0 flex-1 font-mono`}
            value={row.id}
            placeholder="id"
            onChange={(e) => update(index, { id: slugId(e.target.value) })}
          />
          <input
            className={`${inputClass} min-w-0 flex-1`}
            value={row.label}
            placeholder="Label"
            onChange={(e) => update(index, { label: e.target.value })}
          />
          {withDescription && (
            <input
              className={`${inputClass} min-w-0 flex-1`}
              value={row.description || ''}
              placeholder="Description"
              onChange={(e) => update(index, { description: e.target.value })}
            />
          )}
          <button
            type="button"
            className="shrink-0 px-2 text-gray-400 hover:text-red-600"
            onClick={() => onChange(rows.filter((_, i) => i !== index))}
            aria-label="Remove option"
          >
            <TrashIcon className="h-4 w-4" />
          </button>
        </div>
      ))}
      <button
        type="button"
        className="text-sm text-teal-700 font-medium"
        onClick={() => onChange([...(rows || []), { id: '', label: '', order: (rows || []).length + 1 }])}
      >
        Add option
      </button>
    </div>
  )
}

export default function IncidentRegisterConfig() {
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [notice, setNotice] = useState(null)
  const [usingPlatformDefault, setUsingPlatformDefault] = useState(false)
  const [workflows, setWorkflows] = useState([])
  const [categories, setCategories] = useState([])
  const [selections, setSelections] = useState([])
  const [shared, setShared] = useState(null)
  const [assignableRoles, setAssignableRoles] = useState([])
  const [platformDefaults, setPlatformDefaults] = useState(null)
  const [openId, setOpenId] = useState(null)

  const applyPayload = useCallback((data) => {
    setSelections(data.selections || [])
    setShared(data.shared || null)
    setAssignableRoles(data.assignableRoles || [])
    setWorkflows(data.workflows || [])
    setCategories(data.reportCategories || [])
    setUsingPlatformDefault(Boolean(data.usingPlatformDefault))
    setPlatformDefaults(data.platformDefaults || null)
    setOpenId((data.selections || [])[0]?.id || null)
  }, [])

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await authFetch(getLocalApiUrl('/api/mobile/ir-catalog/config'))
      const json = await res.json().catch(() => ({}))
      if (!res.ok || !json.success) throw new Error(json.error || `HTTP ${res.status}`)
      applyPayload(json.data || {})
    } catch (err) {
      setError(err.message || 'Failed to load Incident Register config')
    } finally {
      setLoading(false)
    }
  }, [applyPayload])

  useEffect(() => {
    load()
  }, [load])

  const updateSelection = (index, patch) => {
    setSelections((prev) => prev.map((row, i) => (i === index ? { ...row, ...patch } : row)))
  }

  const updateShared = (patch) => {
    setShared((prev) => ({ ...(prev || {}), ...patch }))
  }

  const updateStage = (stageId, patch) => {
    setShared((prev) => ({
      ...prev,
      stages: (prev?.stages || []).map((stage) => (stage.id === stageId ? { ...stage, ...patch } : stage)),
    }))
  }

  const save = async () => {
    setSaving(true)
    setError(null)
    setNotice(null)
    try {
      const body = {
        selections: selections.map((row, index) => ({
          ...row,
          id: row.id || slugId(row.selectionLabel || row.label) || `type_${index + 1}`,
          cardOrder: Number(row.cardOrder) || index + 1,
          subSelections: (row.subSelections || [])
            .map((opt, optIndex) => ({
              id: opt.id || slugId(opt.label),
              label: String(opt.label || '').trim(),
              order: Number(opt.order) || optIndex + 1,
            }))
            .filter((opt) => opt.id && opt.label)
            .sort((a, b) => a.order - b.order)
            .map((opt, optIndex) => ({ ...opt, order: optIndex + 1 })),
        })),
        shared,
      }
      const res = await authFetch(getLocalApiUrl('/api/mobile/ir-catalog/config'), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
      const json = await res.json().catch(() => ({}))
      if (!res.ok || !json.success) throw new Error(json.error || `HTTP ${res.status}`)
      applyPayload(json.data || {})
      setNotice('Incident Register config saved')
    } catch (err) {
      setError(err.message || 'Failed to save Incident Register config')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return <div className="max-w-5xl mx-auto px-4 py-6 text-sm text-gray-500">Loading Incident Register config…</div>
  }

  return (
    <div className="max-w-5xl mx-auto px-4 py-6 space-y-5">
      <PageHeader
        title="Incident Register config"
        description="Category cards, warning signs, and the shared lists used by capture and the mobile register. Workflows stay fixed."
        actions={
          <div className="flex flex-wrap gap-2">
            <Link
              to="/compliance-safe/incident-register"
              className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              <ArrowLeftIcon className="h-4 w-4" />
              Back to list
            </Link>
            <button
              type="button"
              className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              onClick={() => {
                if (!platformDefaults) return
                setSelections(platformDefaults.selections || [])
                setShared(platformDefaults.shared || null)
                setNotice('Restored platform defaults. Save to keep them.')
              }}
            >
              Reset to defaults
            </button>
            <button
              type="button"
              disabled={saving}
              onClick={save}
              className="rounded-lg bg-teal-700 px-4 py-2 text-sm font-medium text-white hover:bg-teal-800 disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save config'}
            </button>
          </div>
        }
      />

      {usingPlatformDefault && (
        <p className="text-sm text-gray-600">This tenant is using the platform defaults. Save to store a tenant copy.</p>
      )}
      {error && <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm border border-red-100">{error}</div>}
      {notice && <div className="p-3 bg-green-50 text-green-800 rounded-lg text-sm border border-green-100">{notice}</div>}

      <section className="rounded-lg border border-gray-200 bg-white p-4 space-y-3 shadow-sm">
        <div className="flex items-center justify-between gap-3">
          <h2 className="text-sm font-semibold text-gray-900">Incident types</h2>
          <button
            type="button"
            className="inline-flex items-center gap-1 text-sm font-medium text-teal-700"
            onClick={() => {
              const id = `custom_${Date.now()}`
              setSelections((prev) => [
                ...prev,
                {
                  id,
                  label: 'New incident type',
                  selectionLabel: 'New incident type',
                  reportCategory: 'GENERAL',
                  workflow: 'rcg_warning',
                  enabled: true,
                  cardOrder: prev.length + 1,
                  warningSignMin: 1,
                  subSelections: [],
                },
              ])
              setOpenId(id)
            }}
          >
            <PlusIcon className="h-4 w-4" />
            Add type
          </button>
        </div>

        {selections.map((row, index) => {
          const open = openId === row.id
          return (
            <div key={row.id || index} className="border border-gray-200 rounded-lg">
              <button
                type="button"
                className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left bg-gray-50"
                onClick={() => setOpenId(open ? null : row.id)}
              >
                <span className="font-medium text-gray-900">{row.selectionLabel || row.label || 'Untitled'}</span>
                <span className="text-xs text-gray-500">
                  {row.reportCategory} · {workflows.find((w) => w.id === row.workflow)?.label || row.workflow}
                  {row.enabled === false ? ' · hidden' : ''}
                </span>
              </button>
              {open && (
                <div className="p-4 space-y-4 border-t border-gray-100">
                  <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={row.enabled !== false}
                      onChange={(e) => updateSelection(index, { enabled: e.target.checked })}
                    />
                    Show on capture and mobile
                  </label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className={labelClass}>Label</label>
                      <input
                        className={inputClass}
                        value={row.selectionLabel || ''}
                        onChange={(e) => updateSelection(index, { selectionLabel: e.target.value, label: e.target.value })}
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className={labelClass}>Description</label>
                      <input
                        className={inputClass}
                        value={row.description || ''}
                        onChange={(e) => updateSelection(index, { description: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className={labelClass}>Colour</label>
                      <div className="flex gap-2">
                        <input
                          type="color"
                          className="h-10 w-12 shrink-0 rounded border border-gray-300 bg-white p-1"
                          value={/^#[0-9A-Fa-f]{6}$/.test(row.color || '') ? row.color : '#ACC4CC'}
                          onChange={(e) => updateSelection(index, { color: e.target.value.toUpperCase() })}
                        />
                        <input
                          className={`${inputClass} font-mono`}
                          value={row.color || ''}
                          placeholder="#B0E0C1"
                          onChange={(e) => updateSelection(index, { color: e.target.value })}
                        />
                      </div>
                    </div>
                    <div>
                      <label className={labelClass}>Id</label>
                      <input className={`${inputClass} font-mono`} value={row.id || ''} onChange={(e) => updateSelection(index, { id: slugId(e.target.value) })} />
                    </div>
                    <div>
                      <label className={labelClass}>Chip</label>
                      <select
                        className={inputClass}
                        value={row.reportCategory || 'RCG'}
                        onChange={(e) => updateSelection(index, { reportCategory: e.target.value, chip: e.target.value })}
                      >
                        {(categories.length ? categories : [
                          { id: 'RCG', label: 'RCG' },
                          { id: 'RSA', label: 'RSA' },
                          { id: 'GENERAL', label: 'General' },
                        ]).map((c) => (
                          <option key={c.id} value={c.id}>{c.label || c.id}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className={labelClass}>Workflow</label>
                      <select
                        className={inputClass}
                        value={row.workflow || 'rcg_warning'}
                        onChange={(e) => updateSelection(index, { workflow: e.target.value })}
                      >
                        {workflows.map((w) => (
                          <option key={w.id} value={w.id}>{w.label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className={labelClass}>Order</label>
                      <input
                        type="number"
                        className={inputClass}
                        value={row.cardOrder ?? index + 1}
                        onChange={(e) => updateSelection(index, { cardOrder: Number(e.target.value) })}
                      />
                    </div>
                    {row.workflow === 'rcg_warning' && (
                      <div>
                        <label className={labelClass}>Minimum warning signs</label>
                        <input
                          type="number"
                          min="1"
                          className={inputClass}
                          value={row.warningSignMin ?? 1}
                          onChange={(e) => updateSelection(index, { warningSignMin: Number(e.target.value) })}
                        />
                      </div>
                    )}
                  </div>
                  {row.workflow === 'rcg_warning' && (
                    <OptionListEditor
                      title="Warning signs"
                      rows={row.subSelections || []}
                      withOrder
                      onChange={(subSelections) => updateSelection(index, { subSelections })}
                    />
                  )}
                  {row.workflow === 'self_exclusion' && (
                    <>
                      <OptionListEditor
                        title="Breach type"
                        rows={row.breachTypes || []}
                        withDescription
                        onChange={(breachTypes) => updateSelection(index, { breachTypes })}
                      />
                      <OptionListEditor
                        title="Assistance provided"
                        rows={row.assistanceProvided || []}
                        onChange={(assistanceProvided) => updateSelection(index, { assistanceProvided })}
                      />
                      <OptionListEditor
                        title="Actions"
                        rows={row.actions || []}
                        onChange={(actions) => updateSelection(index, { actions })}
                      />
                      <OptionListEditor
                        title="Further actions"
                        rows={row.furtherActions || []}
                        onChange={(furtherActions) => updateSelection(index, { furtherActions })}
                      />
                    </>
                  )}
                  {row.workflow === 'other_concern' && (
                    <>
                      <OptionListEditor
                        title="Exclusion conditions"
                        rows={row.exclusionConditions || []}
                        onChange={(exclusionConditions) => updateSelection(index, { exclusionConditions })}
                      />
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                          <label className={labelClass}>Action</label>
                          <input
                            className={inputClass}
                            value={row.escalateAction?.label || ''}
                            onChange={(e) => updateSelection(index, {
                              escalateAction: { ...(row.escalateAction || {}), label: e.target.value },
                            })}
                          />
                        </div>
                        <div>
                          <label className={labelClass}>Action description</label>
                          <input
                            className={inputClass}
                            value={row.escalateAction?.description || ''}
                            onChange={(e) => updateSelection(index, {
                              escalateAction: { ...(row.escalateAction || {}), description: e.target.value },
                            })}
                          />
                        </div>
                      </div>
                    </>
                  )}
                  <div className="flex justify-end">
                    <button
                      type="button"
                      className="text-sm text-red-600"
                      onClick={() => setSelections((prev) => prev.filter((_, i) => i !== index))}
                    >
                      Remove type
                    </button>
                  </div>
                </div>
              )}
            </div>
          )
        })}
      </section>

      {shared && (
        <section className="rounded-lg border border-gray-200 bg-white p-4 space-y-6 shadow-sm">
          <h2 className="text-sm font-semibold text-gray-900">Shared lists</h2>
          <label className="flex items-start gap-2 text-sm text-gray-800">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={shared.nilOnePerDay === true}
              onChange={(e) => updateShared({ nilOnePerDay: e.target.checked })}
            />
            <span>
              One nil Incident Register report per person per day
              <span className="block text-xs text-gray-500">Off allows more than one. A double tap within 60 seconds still returns the report just saved.</span>
            </span>
          </label>
          <OptionListEditor
            title="Venue areas"
            rows={shared.venueAreas || []}
            onChange={(venueAreas) => updateShared({ venueAreas })}
          />
          <OptionListEditor
            title="RSA incident types"
            rows={shared.rsaIncidentTypes || []}
            onChange={(rsaIncidentTypes) => updateShared({ rsaIncidentTypes })}
          />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className={labelClass}>Voucher amounts</label>
              <AmountField
                amounts={shared.voucherAmounts || []}
                onChange={(voucherAmounts) => updateShared({ voucherAmounts })}
              />
            </div>
            <div>
              <label className={labelClass}>Probable signs before escalation</label>
              <input
                type="number"
                min="1"
                className={inputClass}
                value={shared.escalationThresholds?.probable ?? 2}
                onChange={(e) => updateShared({
                  escalationThresholds: { ...shared.escalationThresholds, probable: Number(e.target.value) },
                })}
              />
            </div>
            <div>
              <label className={labelClass}>General signs before escalation</label>
              <input
                type="number"
                min="1"
                className={inputClass}
                value={shared.escalationThresholds?.general ?? 2}
                onChange={(e) => updateShared({
                  escalationThresholds: { ...shared.escalationThresholds, general: Number(e.target.value) },
                })}
              />
            </div>
          </div>
          <div className="space-y-4">
            <FloorRolePicker
              title="Duty Manager"
              hint="The role told when general signs reach the count above, and on every probable or strong alert. Tick the role this club uses for that job."
              roles={assignableRoles}
              selected={shared.floorAlertRoles?.duty_manager}
              onChange={(duty_manager) => updateShared({
                floorAlertRoles: { ...shared.floorAlertRoles, duty_manager },
              })}
            />
            <FloorRolePicker
              title="RGO"
              hint="The role that can take the probable-sign welfare check with the Duty Manager."
              roles={assignableRoles}
              selected={shared.floorAlertRoles?.rgo}
              onChange={(rgo) => updateShared({
                floorAlertRoles: { ...shared.floorAlertRoles, rgo },
              })}
            />
            <FloorRolePicker
              title="Senior RGO"
              hint="The role told with the Duty Manager when a strong sign is recorded."
              roles={assignableRoles}
              selected={shared.floorAlertRoles?.senior_rgo}
              onChange={(senior_rgo) => updateShared({
                floorAlertRoles: { ...shared.floorAlertRoles, senior_rgo },
              })}
            />
          </div>
          <div className="space-y-4">
            <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">Stage of change</div>
            {(shared.stages || []).map((stage) => (
              <div key={stage.id} className="border border-gray-100 rounded-lg p-3 space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <label className={labelClass}>Stage</label>
                    <input className={`${inputClass} bg-gray-50`} value={stage.id} readOnly />
                  </div>
                  <div className="md:col-span-2">
                    <label className={labelClass}>Label</label>
                    <input
                      className={inputClass}
                      value={stage.label}
                      onChange={(e) => updateStage(stage.id, { label: e.target.value })}
                    />
                  </div>
                </div>
                <div className="flex flex-wrap gap-4 text-sm text-gray-700">
                  <label className="inline-flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={Boolean(stage.requiresSignature)}
                      onChange={(e) => updateStage(stage.id, { requiresSignature: e.target.checked })}
                    />
                    Requires signature
                  </label>
                  <label className="inline-flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={stage.voucher !== false}
                      onChange={(e) => updateStage(stage.id, { voucher: e.target.checked })}
                    />
                    Allow Break in Play voucher
                  </label>
                </div>
                <OptionListEditor
                  title="Assistance"
                  rows={stage.assistance || []}
                  onChange={(assistance) => updateStage(stage.id, { assistance })}
                />
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  )
}
